import axios from '@axios'
import Vue from 'vue'

const state = () => ({
  walletMe: {
    data: { wallet_id: null, customer_id: null, balance: 0 },
  },
  walletTransactions: {
    meta: {
      pagination: {
        page: 1,
        per_page: 50,
        total_objects: 0,
      },
    },
    data: [],
  },
  walletSummary: {
    data: {
      total_balance: 0,
      total_wallets: 0,
      totals: {
        credits_amount: 0,
        debits_amount: 0,
        adjustments_net: 0,
        counts: { credit: 0, debit: 0, adjustment: 0 },
      },
      wallets: [],
      transactions: [],
      meta: {
        wallets_pagination: {
          page: 1,
          per_page: 25,
          total_pages: 1,
          total_objects: 0,
        },
        transactions_pagination: {
          page: 1,
          per_page: 100,
          total_pages: 1,
          total_objects: 0,
        },
      },
    },
  },
  transferReceiptsByCustomer: {},
})

const getters = {
  wallet(state) {
    return state.walletMe.data
  },
  walletBalance(state) {
    return Number(state.walletMe?.data?.balance || 0)
  },
  walletTransactions(state) {
    return state.walletTransactions.data
  },
  walletTxPagination(state) {
    return state.walletTransactions.meta.pagination
  },
  walletSummary(state) {
    return state.walletSummary?.data || null
  },
  transferReceiptsByCustomer: state => customerId => state.transferReceiptsByCustomer[String(customerId || '')] || [],
}

const mutations = {
  SET_WALLET_ME(state, payload) {
    state.walletMe = { data: payload || { wallet_id: null, customer_id: null, balance: 0 } }
  },
  SET_WALLET_BALANCE(state, balance) {
    if (!state.walletMe.data) Vue.set(state.walletMe, 'data', {})
    Vue.set(state.walletMe.data, 'balance', Number(balance || 0))
  },
  CLEAR_WALLET_ME(state) {
    state.walletMe = { data: { wallet_id: null, customer_id: null, balance: 0 } }
  },

  SET_WALLET_TRANSACTIONS(state, payload) {
    state.walletTransactions = payload
  },
  CHANGE_WALLET_TX_PAGINATION(state, { field, value }) {
    state.walletTransactions.meta.pagination[field] = value
  },
  CLEAR_WALLET_TRANSACTIONS(state) {
    state.walletTransactions = {
      meta: { pagination: { page: 1, per_page: 50, total_objects: 0 } },
      data: [],
    }
  },
  SET_WALLET_SUMMARY(state, payload) {
    state.walletSummary = {
      data: payload || {
        total_balance: 0,
        total_wallets: 0,
        totals: {
          credits_amount: 0,
          debits_amount: 0,
          adjustments_net: 0,
          counts: { credit: 0, debit: 0, adjustment: 0 },
        },
        wallets: [],
        transactions: [],
        meta: {
          wallets_pagination: {
            page: 1,
            per_page: 25,
            total_pages: 1,
            total_objects: 0,
          },
          transactions_pagination: {
            page: 1,
            per_page: 100,
            total_pages: 1,
            total_objects: 0,
          },
        },
      },
    }
  },
  SET_TRANSFER_RECEIPTS(state, { customerId, items }) {
    Vue.set(state.transferReceiptsByCustomer, String(customerId || ''), items || [])
  },

}

const actions = {
  fetchWalletMe({ commit, rootGetters }) {
    const token = rootGetters['auth/token']
    return axios
      .get('/v1/wallets/me', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(resp => {
        commit('SET_WALLET_ME', resp.data)
        return resp.data
      })
  },

  debitMe(context, { amount, note = '', metadata = {} }) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/wallets/debit_me', { amount, note, metadata })
        .then(resp => {
          context.commit('SET_WALLET_BALANCE', resp.data?.balance)
          context.dispatch('fetchMyTransactions', { limit: context.getters.walletTxPagination.per_page }).catch(() => {})
          resolve(resp.data)
        })
        .catch(err => reject(err?.response || err))
    })
  },

  creditMe(context, { amount, note = '', metadata = {} }) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/wallets/credit_me', { amount, note, metadata })
        .then(resp => {
          context.commit('SET_WALLET_BALANCE', resp.data?.balance)
          context.dispatch('fetchMyTransactions', { limit: context.getters.walletTxPagination.per_page }).catch(() => {})
          resolve(resp.data)
        })
        .catch(err => reject(err?.response || err))
    })
  },

  adjustMe(context, { delta, note = '', metadata = {} }) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/wallets/adjust_me', { delta, note, metadata })
        .then(resp => {
          context.commit('SET_WALLET_BALANCE', resp.data?.balance)
          context.dispatch('fetchMyTransactions', { limit: context.getters.walletTxPagination.per_page }).catch(() => {})
          resolve(resp.data)
        })
        .catch(err => reject(err?.response || err))
    })
  },

  // ----- Helpers UI -----
  changeWalletTxPagination(context, { field, value }) {
    context.commit('CHANGE_WALLET_TX_PAGINATION', { field, value })
    const { per_page: perPage } = context.getters.walletTxPagination
    return context.dispatch('fetchMyTransactions', { limit: perPage })
  },

  refreshWallet(context) {
    return Promise.all([
      context.dispatch('fetchWalletMe'),
      context.dispatch('fetchMyTransactions', { limit: context.getters.walletTxPagination.per_page }),
    ])
  },

  fetchWalletByCustomer(context, payload) {
    const customerId = typeof payload === 'string' ? payload : payload?.customerId
    return axios.get('/v1/wallets/by_customer', { params: { customer_id: customerId } })
      .then(resp => {
        context.commit('SET_WALLET_ME', resp.data)
        return resp.data
      })
  },

  async fetchWalletAndTransactionsByCustomer({ dispatch }, { customerId, limit = 200 }) {
    const w = await dispatch('fetchWalletByCustomer', { customerId })
    if (!w?.wallet_id) return []
    return dispatch('fetchTransactionsByWalletId', { walletId: w.wallet_id, limit })
  },

  fetchMyTransactions(context, params = {}) {
    const { page, per_page: perPage } = context.getters.walletTxPagination
    const limit = params.limit || perPage
    return axios.get('/v1/wallets/me/transactions', { params: { limit } })
      .then(resp => {
        const items = Array.isArray(resp.data) ? resp.data : []
        context.commit('SET_WALLET_TRANSACTIONS', {
          data: items,
          meta: { pagination: { page, per_page: limit, total_objects: items.length } },
        })
        return items
      })
  },

  fetchTransactionsByWalletId(context, { walletId, limit = 200 }) {
    const { page, per_page: perPage } = context.getters.walletTxPagination
    const use = limit || perPage
    return axios
      .get(`/v1/wallets/${walletId}/transactions`, { params: { limit: use } })
      .then(resp => {
        const items = Array.isArray(resp.data) ? resp.data : []
        context.commit('SET_WALLET_TRANSACTIONS', {
          data: items,
          meta: { pagination: { page, per_page: use, total_objects: items.length } },
        })
        return items
      })
  },

  fetchWalletSummary({ commit }, {
    limit,
    walletsPage,
    walletsPerPage,
    transactionsPage,
    transactionsPerPage,
  } = {}) {
    const params = {}
    if (limit) params.limit = limit
    if (walletsPage) params.wallets_page = walletsPage
    if (walletsPerPage) params.wallets_per_page = walletsPerPage
    if (transactionsPage) params.transactions_page = transactionsPage
    if (transactionsPerPage) params.transactions_per_page = transactionsPerPage

    return axios
      .get('/v1/wallets/summary', { params })
      .then(resp => {
        commit('SET_WALLET_SUMMARY', resp.data)
        return resp.data
      })
  },

  async creditByCustomer({ dispatch }, { customerId, amount, note = '' }) {
    const w = await dispatch('fetchWalletByCustomer', { customerId })
    if (!w?.wallet_id) throw new Error('No se encontró wallet del cliente')

    const resp = await axios.post(`/v1/wallets/${w.wallet_id}/credit`, { amount, note })
    await Promise.all([
      dispatch('fetchWalletByCustomer', { customerId }),
      dispatch('fetchTransactionsByWalletId', { walletId: w.wallet_id, limit: 500 }),
    ])
    return resp.data
  },

  async adjustByCustomer({ dispatch }, { customerId, delta, note = '' }) {
    const w = await dispatch('fetchWalletByCustomer', { customerId })
    if (!w?.wallet_id) throw new Error('No se encontró wallet del cliente')

    const resp = await axios.post(`/v1/wallets/${w.wallet_id}/adjust`, { delta, note })
    await Promise.all([
      dispatch('fetchWalletByCustomer', { customerId }),
      dispatch('fetchTransactionsByWalletId', { walletId: w.wallet_id, limit: 500 }),
    ])
    return resp.data
  },

  approveTransferReceipt(_, { receiptId }) {
    return axios.put(`/v1/transfer_receipts/${receiptId}/approve`).then(r => r.data)
  },

  rejectTransferReceipt(_, { receiptId, reason }) {
    return axios.put(`/v1/transfer_receipts/${receiptId}/reject`, { reason }).then(r => r.data)
  },

  fetchTransferReceiptsByWalletId({ commit }, { walletId, customerId }) {
    return axios
      .get(`/v1/wallets/${walletId}/transfer_receipts`)
      .then(resp => {
        let items = []

        if (Array.isArray(resp.data?.data)) {
        // cuando backend responde { data: [...] , meta: ... }
          items = resp.data.data
        } else if (Array.isArray(resp.data)) {
        // cuando backend responde directamente [...]
          items = resp.data
        }

        commit('SET_TRANSFER_RECEIPTS', { customerId, items })
        return items
      })
  },

  uploadTransferReceiptByWalletId(_, { walletId, receipt }) {
    return axios.post(`/v1/wallets/${walletId}/transfer_receipts`, { receipt }, { skipErrorToast: true })
  },

}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
